import { View, Text, ScrollView, Pressable, StyleSheet, RefreshControl, Platform, Image, LayoutAnimation, useWindowDimensions, AccessibilityInfo } from 'react-native'
import Animated, { FadeInRight, FadeInDown, useSharedValue, useAnimatedStyle, withRepeat, withTiming } from 'react-native-reanimated'
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useFocusEffect, useLocalSearchParams } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useRouter } from 'expo-router'
import { Mail, MessageSquare, ArrowRight, RefreshCw, ChevronDown, Paperclip, Info, Download, X, AlertCircle, ShieldCheck, FileCheck, Lock, Sparkles, PenLine, Bell, Check } from 'lucide-react-native'
import { apiClient } from '../../src/api/client'
import { toast } from '../../src/components/Toast'
import EmailBody from '../../src/components/EmailBody'
import EmailProviderModal from '../../src/components/EmailProviderModal'
import PressableScale from '../../src/components/PressableScale'
import AnimatedItem from '../../src/components/AnimatedItem'
import { ActionCard } from '../../src/components/ActionCard'
import { InboxSkeleton } from '../../src/components/Skeleton'
import { colors, spacing, borderRadius, typography, shadows } from '../../src/theme'
import { events } from '../../src/lib/posthog'
import { feedbackTrigger } from '../../src/components/FeedbackButton'
import { useFeedback } from '../../src/hooks/useFeedback'
import { useDataLoading } from '../../src/hooks/useDataLoading'
import {
  Message, InboxThread, filterForInbox, groupIntoThreads,
  getMessagePreview, formatShortDate, getThreadBadges, getProviderColor, stripSubjectPrefix,
  needsAction, isAwaitingReply, computeTabCounts,
} from '../../src/utils/threading'
import ContentContainer from '../../src/components/ContentContainer'
import { getProviderLogo } from '../../src/utils/providerLogos'
import { appEvents } from '../../src/lib/events'
import { haptics } from '../../src/utils/haptics'
import { useAuth } from '../../src/auth/AuthProvider'
import { sentryCaptureException } from '../../src/lib/sentry'
import { useResponsive } from '../../src/hooks/useResponsive'
import { LinearGradient } from 'expo-linear-gradient'

export { ScreenErrorBoundary as ErrorBoundary } from '../../src/components/ScreenErrorBoundary'

type TabKey = 'modtaget' | 'afventer' | 'regninger' | 'varsler' | 'fuldmagter' | 'sendt' | 'alle'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'alle', label: 'Alle' },
  { key: 'modtaget', label: 'Kræver handling' },
  { key: 'afventer', label: 'Afventer svar' },
  { key: 'regninger', label: 'Regninger' },
  { key: 'varsler', label: 'Varsler' },
  { key: 'fuldmagter', label: 'Fuldmagter' },
  { key: 'sendt', label: 'Sendt' },
]

export default function InboxScreen() {
  const router = useRouter()
  const params = useLocalSearchParams<{ email_connected?: string }>()
  const { user } = useAuth()
  const { isDesktop } = useResponsive()
  const [messages, setMessages] = useState<Message[]>([])
  const { loading, refreshing, loadError, setLoading, setRefreshing, setLoadError } = useDataLoading()
  const [activeTab, setActiveTab] = useState<TabKey>('alle')
  const [hasActiveHunt, setHasActiveHunt] = useState(false)
  const userSelectedTabRef = useRef(false)
  const { shouldFireTrigger } = useFeedback()
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [emailConnected, setEmailConnected] = useState<boolean | null>(null)
  const [showEmailModal, setShowEmailModal] = useState(false)

  const handleEmailConnected = useCallback(() => {
    setShowEmailModal(false)
    setEmailConnected(true)
    apiClient.email.status().then((res: any) => {
      const connections = res?.data?.connections || []
      const newest = connections.length > 0 ? connections.reduce((a: any, b: any) =>
        new Date(b.connected_at) > new Date(a.connected_at) ? b : a) : null
      if (newest?.id && !newest.scan_consent_given_at) {
        apiClient.email.scanConsent(newest.id).catch(() => {})
      }
    }).catch(() => {})
    toast.success('Email forbundet!')
    loadInboxData()
  }, [])

  // Inline accordion state
  const [expandedThreadKey, setExpandedThreadKey] = useState<string | null>(null)
  const [showAllMessages, setShowAllMessages] = useState(false)
  const activeThreadKeyRef = useRef<string | null>(null)

  const loadInboxData = useCallback(async () => {
    try {
      setLoadError(false)
      const res = await apiClient.inbox.list({ limit: '200' }).catch(() => ({ data: { messages: [] } }))
      const filtered = filterForInbox(res.data?.messages || [])
      setMessages(filtered)
      const reqRes = user?.id ? await apiClient.quotes.getRequests(user.id).catch(() => ({ data: [] })) : { data: [] }
      setHasActiveHunt((reqRes.data || []).some((r: any) =>
        ['pending', 'pending_approval', 'sent'].includes(r.status)
      ))
      if (filtered.length > 0 && shouldFireTrigger('inbox_explored')) {
        feedbackTimerRef.current = setTimeout(() => feedbackTrigger.open('inbox_explored', 'Er det nemt at finde rundt?'), 20_000)
      }
    } catch (error: any) {
      sentryCaptureException(error, { screen: 'inbox' })
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    events.inboxOpened(); loadInboxData()
    const unsub = appEvents.on('inbox-updated', () => loadInboxData())
    return () => { unsub(); if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current) }
  }, [])

  useFocusEffect(
    useCallback(() => {
      apiClient.email.status().then((res: any) => {
        const connections = res?.data?.connections || []
        setEmailConnected(connections.some((c: any) => c.is_active))
      }).catch(() => setEmailConnected(false))
    }, [])
  )

  // Handle OAuth redirect — sync + scan-consent when user returns from email OAuth
  useEffect(() => {
    if (params.email_connected === 'true') {
      apiClient.email.sync().then((res: any) => {
        const connections = res?.data?.connections || []
        if (connections.length > 0) {
          setEmailConnected(true)
          toast.success('Email forbundet!')
          const newest = connections.reduce((a: any, b: any) =>
            new Date(b.connected_at) > new Date(a.connected_at) ? b : a)
          if (newest?.id && !newest.scan_consent_given_at) {
            apiClient.email.scanConsent(newest.id).catch(() => {})
          }
          loadInboxData()
        }
      }).catch(() => {})
    } else if (params.email_connected === 'false') {
      toast.error('Email tilslutning fejlede')
    }
  }, [params.email_connected])

  const onRefresh = useCallback(async () => {
    haptics.light()
    setRefreshing(true)
    await loadInboxData()
    setRefreshing(false)
  }, [loadInboxData])

  const visibleMessages = useMemo(
    () => messages.filter(m => {
      if (m.message_type === 'system' && m.direction === 'outbound') {
        return m.parsed_data?.email_type === 'fuldmagt' || m.parsed_data?.type === 'switch_request_sent'
      }
      return true
    }),
    [messages]
  )
  const threads = useMemo(() => groupIntoThreads(visibleMessages), [visibleMessages])

  const filteredThreads = useMemo(() => {
    if (activeTab === 'alle') return threads

    return threads
      .map(thread => {
        // Status-based tabs: show full thread for context
        if (activeTab === 'modtaget') return needsAction(thread) ? thread : null
        if (activeTab === 'afventer') return isAwaitingReply(thread) ? thread : null

        // Type-based tabs: filter to only relevant messages
        let filtered: Message[]
        if (activeTab === 'regninger') {
          filtered = thread.messages.filter(m => m.message_type === 'bill' || m.parsed_data?.classification === 'bill')
        } else if (activeTab === 'varsler') {
          filtered = thread.messages.filter(m => m.parsed_data?.classification === 'notice')
        } else if (activeTab === 'fuldmagter') {
          filtered = thread.messages.filter(m =>
            m.parsed_data?.email_type === 'fuldmagt' ||
            m.parsed_data?.classification === 'fuldmagt_confirmation' ||
            m.subject?.toLowerCase().includes('fuldmagt')
          )
        } else if (activeTab === 'sendt') {
          filtered = thread.messages.filter(m => m.direction === 'outbound')
        } else {
          return thread
        }
        if (filtered.length === 0) return null
        return { ...thread, messages: filtered, latestMessage: filtered[filtered.length - 1] }
      })
      .filter((t): t is InboxThread => t !== null)
  }, [threads, activeTab])

  const tabCounts = useMemo(() => computeTabCounts(threads), [threads])
  const unreadTotal = useMemo(() => threads.reduce((sum, t) => sum + t.unreadCount, 0), [threads])
  const actionCount = tabCounts.modtaget


  const markAsRead = async (messageId: string) => {
    try {
      await apiClient.inbox.markRead(messageId)
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, status: 'read' as const, read_at: new Date().toISOString() } : m))
      appEvents.emitDebounced('inbox-updated')
    } catch {
      // API interceptor handles error reporting
    }
  }

  const toggleThread = useCallback((thread: InboxThread) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)

    if (expandedThreadKey === thread.threadKey) {
      // Collapse
      setExpandedThreadKey(null)
      activeThreadKeyRef.current = null
      return
    }

    // Expand
    setExpandedThreadKey(thread.threadKey)
    activeThreadKeyRef.current = thread.threadKey
    setShowAllMessages(thread.messages.length <= 5)
    haptics.medium()
    events.messageRead(thread.threadKey)

    // Mark unread as read
    thread.messages.filter(m => m.status === 'unread').forEach(m => markAsRead(m.id))

    // Lazy fetch full bodies for scanned emails
    const toFetch = thread.messages.filter(m => (m as any).original_email_id && !(m as any)._bodyLoaded)
    if (toFetch.length === 0) return

    const threadKey = thread.threadKey
    Promise.allSettled(
      toFetch.map(msg =>
        apiClient.inbox.getFullBody(msg.id).then((res: any) => ({ id: msg.id, ...res.data }))
      )
    ).then(results => {
      if (activeThreadKeyRef.current !== threadKey) return
      const failed = results.filter(r => r.status === 'rejected').length
      if (failed > 0) toast.info(`Kunne ikke hente ${failed} email${failed !== 1 ? 's' : ''} brødtekst`)
      const updateMap = new Map(
        results
          .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled')
          .map(r => [r.value.id, { body: r.value.body, body_html: r.value.body_html }])
      )
      if (updateMap.size === 0) return
      setMessages(prev => prev.map(m =>
        updateMap.has(m.id) ? { ...m, ...updateMap.get(m.id), _bodyLoaded: true } as any : m
      ))
    })
  }, [expandedThreadKey, markAsRead])

  const handleDownloadAttachment = useCallback(async (msgId: string, att: any) => {
    if (!att.id) {
      toast.info('Vedhæftning ikke tilgængelig — email-forbindelse mangler')
      return
    }
    try {
      haptics.light()
      if (Platform.OS === 'web') {
        const res = await apiClient.inbox.downloadAttachment(msgId, att.id)
        const blob = new Blob([res.data], { type: att.content_type || att.mime_type || 'application/octet-stream' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.rel = 'noopener'
        const mimeType = (att.content_type || att.mime_type || '').toLowerCase()
        if (!mimeType.startsWith('application/pdf') && !mimeType.startsWith('image/')) {
          link.download = att.filename || att.name || 'attachment'
        }
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      } else {
        const res = await apiClient.inbox.downloadAttachment(msgId, att.id)
        const Sharing = await import('expo-sharing')
        const FS = await import('expo-file-system/legacy')
        const filename = att.filename || att.name || 'attachment'
        const fileUri = `${FS.cacheDirectory}${filename}`
        const bytes = new Uint8Array(res.data)
        let binary = ''
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
        const base64 = btoa(binary)
        await FS.writeAsStringAsync(fileUri, base64, { encoding: FS.EncodingType.Base64 })
        await Sharing.shareAsync(fileUri, { mimeType: att.content_type || att.mime_type || 'application/octet-stream' })
      }
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 404) {
        toast.error('Vedhæftning ikke tilgængelig — email-forbindelse mangler')
      } else {
        toast.error('Kunne ikke åbne vedhæftning')
      }
    }
  }, [])

  if (loading) {
    return (
      <SafeAreaView style={styles.container} edges={[]}>
        <View style={styles.header}>
          <Text style={styles.title}>Indbakke</Text>
        </View>
        <InboxSkeleton />
      </SafeAreaView>
    )
  }

  // Full-screen takeover: while email isn't connected, the entire Inbox tab
  // promotes the email connection. The actual thread list (from agent_email)
  // is hidden until the user connects, so the value prop is unmistakable.
  if (emailConnected === false) {
    return (
      <SafeAreaView style={styles.container} edges={[]}>
        <ContentContainer>
          <ConnectEmailEmptyState
            hasActiveHunt={hasActiveHunt}
            threadCount={threads.length}
            onConnect={() => { haptics.light(); setShowEmailModal(true) }}
            refreshing={refreshing}
            onRefresh={onRefresh}
            currentProviders={user?.current_providers || null}
          />
        </ContentContainer>

        <EmailProviderModal
          visible={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onConnected={handleEmailConnected}
          redirectTo="inbox"
          consentGiven={true}
        />
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView style={styles.container} edges={[]}>
      <ContentContainer>
        <ScrollView
          contentContainerStyle={{ paddingBottom: 100, flexGrow: 1 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
        >
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>Indbakke</Text>
              <Text style={styles.subtitle}>Beskeder fra dine udbydere</Text>
            </View>
            <Pressable accessibilityRole="button" accessibilityLabel="Opdater indbakke" style={styles.refreshBtn} onPress={() => { setLoading(true); loadInboxData() }}>
              <RefreshCw size={18} color={colors.text.muted} />
            </Pressable>
          </View>

          {loadError && (
            <View style={styles.errorBanner}>
              <Info size={16} color="#92400e" />
              <Text style={styles.errorText}>Noget gik galt — vores team er notificeret.</Text>
              <Pressable accessibilityRole="button" accessibilityLabel="Prøv igen" onPress={() => { setLoading(true); loadInboxData() }}>
                <Text style={styles.errorRetry}>Prøv igen</Text>
              </Pressable>
            </View>
          )}

          {threads.length > 0 && (
            <View style={styles.summaryBar}>
              <View style={styles.summaryCard}>
                <Text style={styles.summaryValue}>{threads.length}</Text>
                <Text style={styles.summaryLabel}>Selskaber</Text>
              </View>
              <View style={styles.summaryCard}>
                <Text style={[styles.summaryValue, unreadTotal > 0 && { color: colors.primary }]}>{unreadTotal}</Text>
                <Text style={styles.summaryLabel}>Ulæste</Text>
              </View>
              <View style={styles.summaryCard}>
                <Text style={[styles.summaryValue, actionCount > 0 && { color: colors.primary }]}>{actionCount}</Text>
                <Text style={styles.summaryLabel}>Modtaget</Text>
              </View>
            </View>
          )}

          {threads.length > 0 && (
            <View style={styles.tabsWrap}>
              {TABS.map((tab) => {
                const count = tabCounts[tab.key]
                const isActive = activeTab === tab.key
                return (
                  <PressableScale
                    key={tab.key}
                    accessibilityRole="tab"
                    accessibilityState={{ selected: isActive }}
                    accessibilityLabel={`${tab.label}${count > 0 ? `, ${count} stk` : ''}`}
                    style={[styles.tab, isActive && styles.tabActive]}
                    onPress={() => { haptics.selection(); userSelectedTabRef.current = true; setActiveTab(tab.key) }}
                    haptic="light"
                  >
                    <Text style={[styles.tabText, isActive && styles.tabTextActive]}>
                      {tab.label}{count > 0 ? ` ${count}` : ''}
                    </Text>
                  </PressableScale>
                )
              })}
            </View>
          )}

          {/* Thread list with inline accordion */}
          {filteredThreads.length > 0 ? (
            filteredThreads.map((thread, index) => {
              const isExpanded = expandedThreadKey === thread.threadKey
              const msgs = thread.messages
              const visible = isExpanded
                ? (showAllMessages ? msgs : msgs.length <= 5 ? msgs : [msgs[0], ...msgs.slice(-3)])
                : []
              const hiddenCount = msgs.length - (showAllMessages ? msgs.length : msgs.length <= 5 ? msgs.length : 4)

              const lastMessage = msgs[msgs.length - 1]
              const isQuoteThread = msgs.some(m => m.message_type === 'quote')
              const showReplyButton = isExpanded && lastMessage?.direction === 'inbound' && !isQuoteThread
              const isBillThread = msgs.some(m => m.message_type === 'bill' || m.parsed_data?.classification === 'bill')
              const isNoticeThread = msgs.some(m => m.parsed_data?.classification === 'notice')
              const replyCta = isBillThread
                ? 'Snak med agent om regningen'
                : isNoticeThread
                  ? 'Se dine muligheder for at skifte'
                  : 'Svar via AI-rådgiver'

              return (
                <AnimatedItem key={thread.threadKey} index={index}>
                  <ThreadRow thread={thread} expanded={isExpanded} onPress={() => toggleThread(thread)} />
                  {isExpanded && (
                    <View style={styles.expandedContainer}>
                      {visible.map((msg, idx) => (
                        <View key={msg.id}>
                          {idx === 1 && !showAllMessages && hiddenCount > 0 && (
                            <Pressable
                              accessibilityRole="button"
                              accessibilityLabel={`Vis ${hiddenCount} ældre ${hiddenCount === 1 ? 'besked' : 'beskeder'}`}
                              style={styles.showOlderBtn}
                              onPress={() => { LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut); setShowAllMessages(true) }}
                            >
                              <ChevronDown size={12} color={colors.primary} />
                              <Text style={styles.showOlderText}>Vis {hiddenCount} ældre {hiddenCount === 1 ? 'besked' : 'beskeder'}</Text>
                            </Pressable>
                          )}
                          <ThreadMessage
                            msg={msg}
                            thread={thread}
                            onDownloadAttachment={handleDownloadAttachment}
                            onRespond={() => { appEvents.emit('inbox-updated'); setExpandedThreadKey(null) }}
                          />
                        </View>
                      ))}
                      {showReplyButton && (
                        <PressableScale
                          accessibilityRole="button"
                          accessibilityLabel="Svar via AI-rådgiver"
                          style={styles.replyButton}
                          haptic="medium"
                          onPress={() => {
                            const lastInbound = msgs.filter(m => m.direction === 'inbound').pop()
                            setExpandedThreadKey(null)
                            router.push(`/(tabs)/chat?inbox_message_id=${lastInbound?.id || ''}&provider=${encodeURIComponent(thread.providerName)}` as any)
                          }}
                        >
                          <MessageSquare size={16} color={colors.text.inverse} />
                          <Text style={styles.replyButtonText}>{replyCta}</Text>
                          <ArrowRight size={14} color={colors.text.inverse} style={{ opacity: 0.6, marginLeft: 2 }} />
                        </PressableScale>
                      )}
                    </View>
                  )}
                </AnimatedItem>
              )
            })
          ) : threads.length > 0 ? (
            activeTab === 'modtaget' ? (
              <View style={styles.emptyTab}>
                <View style={[styles.emptyIcon, { backgroundColor: colors.primaryLight }]}>
                  <Mail size={32} color={colors.primary} />
                </View>
                <Text style={styles.emptyTabTitle}>Din agent venter på svar</Text>
                <Text style={styles.emptyTabText}>Svar fra udbydere kommer typisk inden 1-3 hverdage.</Text>
              </View>
            ) : activeTab === 'afventer' ? (
              <View style={styles.emptyTab}>
                <View style={[styles.emptyIcon, { backgroundColor: colors.successLight }]}>
                  <Mail size={32} color={colors.success} />
                </View>
                <Text style={styles.emptyTabTitle}>Alle har svaret</Text>
                <Text style={styles.emptyTabText}>Godt gået — alle kontaktede selskaber har svaret.</Text>
              </View>
            ) : (
              <View style={styles.emptyTab}>
                <View style={[styles.emptyIcon, { backgroundColor: colors.surface }]}>
                  <Mail size={32} color={colors.text.muted} />
                </View>
                <Text style={styles.emptyTabTitle}>
                  {activeTab === 'regninger' ? 'Ingen regninger endnu' :
                   activeTab === 'varsler' ? 'Ingen varsler' :
                   activeTab === 'fuldmagter' ? 'Ingen fuldmagter endnu' :
                   activeTab === 'sendt' ? 'Ingen sendte beskeder' :
                   'Ingen beskeder endnu'}
                </Text>
                <Text style={styles.emptyTabText}>
                  {activeTab === 'varsler' ? 'Alt ser fint ud — ingen prisændringer eller opsigelser.' :
                   'Beskeder vises her når de bliver tilgængelige.'}
                </Text>
              </View>
            )
          ) : hasActiveHunt ? (
            <View style={styles.emptyInbox}>
              <View style={[styles.emptyInboxIcon, { backgroundColor: colors.primaryLight }]}>
                <Mail size={28} color={colors.primary} />
              </View>
              <Text style={styles.emptyInboxTitle}>Jagten er i gang</Text>
              <Text style={styles.emptyInboxText}>
                Vi har kontaktet selskaberne for dig. Svar vises her, når de kommer ind.
              </Text>
            </View>
          ) : (
            <View style={styles.emptyInbox}>
              <View style={styles.emptyInboxIcon}>
                <Mail size={28} color={colors.text.muted} />
              </View>
              <Text style={styles.emptyInboxTitle}>Indbakken er tom</Text>
              <Text style={styles.emptyInboxText}>
                Når du starter en jagt, sender vi fuldmagter og forespørgsler til selskaberne. Svar vises her.
              </Text>
              <Pressable accessibilityRole="button" accessibilityLabel="Start ny jagt" style={styles.emptyInboxCta} onPress={() => router.push('/onboarding' as any)}>
                <Text style={styles.emptyInboxCtaText}>Start ny jagt</Text>
              </Pressable>
            </View>
          )}
        </ScrollView>
      </ContentContainer>
    </SafeAreaView>
  )
}

/* ─── Connect-email empty state ─── */

function ConnectEmailEmptyState({
  hasActiveHunt,
  threadCount,
  onConnect,
  refreshing,
  onRefresh,
  currentProviders,
}: {
  hasActiveHunt: boolean
  threadCount: number
  onConnect: () => void
  refreshing: boolean
  onRefresh: () => void
  currentProviders: Record<string, { provider_id: string; provider_name: string }> | null
}) {
  const { height: screenHeight, width: screenWidth } = useWindowDimensions()
  // Desktop = web + width ≥ 1024 (matches useResponsive() in parent)
  const isWideDesktop = Platform.OS === 'web' && screenWidth >= 1024
  const isMobile = screenWidth < 768
  const [reduceMotion, setReduceMotion] = useState(false)

  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled().then(setReduceMotion)
    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', setReduceMotion)
    return () => sub.remove()
  }, [])

  // CTA shadow pulse — subtle opacity oscillation to draw the eye without jitter.
  const pulse = useSharedValue(0)
  useEffect(() => {
    if (reduceMotion) { pulse.value = 0; return }
    pulse.value = withRepeat(withTiming(1, { duration: 1800 }), -1, true)
  }, [reduceMotion])
  const ctaAnimStyle = useAnimatedStyle(() => ({
    shadowOpacity: 0.25 + pulse.value * 0.2,
    shadowRadius: 8 + pulse.value * 4,
  }))

  // Responsive lerp — same pattern as login.tsx / what-matters.tsx
  const s = Math.min(1, Math.max(0, (screenHeight - 667) / (932 - 667)))
  const lerp = (min: number, max: number) => Math.round(min + (max - min) * s)
  const isCompact = screenHeight < 820
  const isUltraCompact = screenHeight < 700

  const lockIconSize = lerp(44, 52)
  const lockIconInnerSize = lerp(20, 24)
  const lockedTitleFontSize = lerp(20, 24)

  // 3 key benefits inside locked card — framed so it's clear user stays in control
  const benefits = [
    'Alle regninger samlet ét sted',
    'Varsler om prisstigninger',
    'Agenten skriver svar du godkender',
  ]

  // Build previews from user's actual providers when available; fallback to generic demo.
  // Each category maps to a mock subject/tag that matches that provider-type.
  const mockByCategory: Record<string, { subject: string; tag: string; tagColor: string; tagBg: string; time: string }> = {
    energy: {
      subject: 'Din elregning for marts',
      tag: 'Regning',
      tagColor: colors.primary,
      tagBg: colors.primaryLight,
      time: 'i dag',
    },
    mobile: {
      subject: 'Vi hæver din pris pr. 1. maj',
      tag: 'Varsel',
      tagColor: '#b45309',
      tagBg: '#fef3c7',
      time: 'i går',
    },
    internet: {
      subject: 'Nyt tilbud: Spar 1.240 kr/år',
      tag: 'Tilbud',
      tagColor: '#059669',
      tagBg: '#d1fae5',
      time: '2 dage',
    },
    broadband: {
      subject: 'Nyt tilbud: Spar 1.240 kr/år',
      tag: 'Tilbud',
      tagColor: '#059669',
      tagBg: '#d1fae5',
      time: '2 dage',
    },
  }
  const userProviderCats = Object.entries(currentProviders || {})
    .filter(([, v]) => v?.provider_name)
    .slice(0, 3)
  const hasUserProviders = userProviderCats.length >= 2

  // Build preview list — always generates 6 realistic entries (user providers + fallback fillers)
  // First 2 sharp (above lock), next 3-4 faded (below lock) to fill the page.
  const fallbackPreviews = [
    { provider: 'Norlys', subject: 'Din elregning for marts', tag: 'Regning', tagColor: colors.primary, tagBg: colors.primaryLight, time: 'i dag', unread: true },
    { provider: 'Telenor', subject: 'Vi hæver din pris pr. 1. maj', tag: 'Varsel', tagColor: '#b45309', tagBg: '#fef3c7', time: 'i går', unread: true },
    { provider: 'Andel Energi', subject: 'Nyt tilbud: Spar 1.240 kr/år', tag: 'Tilbud', tagColor: '#059669', tagBg: '#d1fae5', time: '2 dage', unread: true },
    { provider: 'YouSee', subject: 'Faktureringsgebyr: 30 kr', tag: 'Gebyr', tagColor: '#b91c1c', tagBg: '#fee2e2', time: '4. apr', unread: false },
    { provider: 'CBB', subject: 'Dit nye abonnement er aktivt', tag: 'Skift', tagColor: '#6366f1', tagBg: '#e0e7ff', time: '2. apr', unread: false },
    { provider: '3', subject: 'Velkommen til 3 — her er dine vilkår', tag: 'Velkomst', tagColor: '#0891b2', tagBg: '#cffafe', time: '28. mar', unread: false },
    { provider: 'OK Mobil', subject: 'Påmindelse: Din binding udløber 15. maj', tag: 'Påmindelse', tagColor: '#b45309', tagBg: '#fef3c7', time: '26. mar', unread: false },
    { provider: 'Fibia', subject: 'Kvittering for betaling — 349 kr', tag: 'Kvittering', tagColor: colors.primary, tagBg: colors.primaryLight, time: '24. mar', unread: false },
    { provider: 'Ewii', subject: 'Din årsopgørelse er klar', tag: 'Regning', tagColor: colors.primary, tagBg: colors.primaryLight, time: '22. mar', unread: false },
    { provider: 'Telia', subject: 'Fornyelse: Vælg din nye aftale', tag: 'Fornyelse', tagColor: '#0891b2', tagBg: '#cffafe', time: '19. mar', unread: false },
    { provider: 'Stofa', subject: 'Velkomstrabat: 3 mdr halv pris', tag: 'Rabat', tagColor: '#059669', tagBg: '#d1fae5', time: '17. mar', unread: false },
    { provider: 'EnergiFyn', subject: 'Dit forbrug steg 18% sidste måned', tag: 'Varsel', tagColor: '#b45309', tagBg: '#fef3c7', time: '15. mar', unread: false },
  ]
  const userPreviews = userProviderCats.map(([cat, v], i) => ({
    provider: v.provider_name,
    unread: i < 2,
    ...(mockByCategory[cat] || mockByCategory.energy),
  }))
  // Combine user + fallback to always get 6. Dedup by provider name.
  const seen = new Set(userPreviews.map(p => p.provider.toLowerCase()))
  const previewsAll = hasUserProviders
    ? [...userPreviews, ...fallbackPreviews.filter(p => !seen.has(p.provider.toLowerCase()))].slice(0, 12)
    : fallbackPreviews
  // Card floats as absolute overlay — peek content below shows what's locked.
  // Mobile: keep 2 sharp at top, 2 faded below (so something is visible behind the card center).
  // Desktop: full 2 sharp + 10 faded to fill the page with rich inbox context.
  const sharpPreviews = previewsAll.slice(0, 2)
  const fadedCount = isMobile ? 2 : 10
  const fadedPreviews = previewsAll.slice(2, 2 + fadedCount)

  // Mock summary numbers — feel realistic for a connected inbox
  const mockSelskaber = hasUserProviders ? userProviderCats.length + 5 : 8
  const mockUlaeste = 3
  const mockModtaget = 27

  return (
    <View style={styles.connectWrap}>
      {/* Real inbox header — matches connected state so it feels like "same page, just locked" */}
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Indbakke</Text>
          <Text style={styles.subtitle}>Beskeder fra dine udbydere</Text>
        </View>
        <View style={styles.refreshBtn}>
          <RefreshCw size={18} color={colors.text.muted} style={{ opacity: 0.4 }} />
        </View>
      </View>

      {/* Mock summary bar — muted, realistic numbers for "here's what's coming" */}
      <View style={[styles.summaryBar, styles.connectMockMuted]}>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryValue}>{mockSelskaber}</Text>
          <Text style={styles.summaryLabel}>Selskaber</Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={[styles.summaryValue, { color: colors.primary }]}>
            {mockUlaeste}
          </Text>
          <Text style={styles.summaryLabel}>Ulæste</Text>
        </View>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryValue}>{mockModtaget}</Text>
          <Text style={styles.summaryLabel}>Modtaget</Text>
        </View>
      </View>

      {/* Mock tab bar — all tabs visible, "Alle" active, rest muted so user sees structure */}
      <View style={[styles.tabsWrap, styles.connectMockMuted]} pointerEvents="none">
        {TABS.map((tab) => {
          const isActive = tab.key === 'alle'
          return (
            <View
              key={tab.key}
              style={[styles.tab, isActive && styles.tabActive]}
            >
              <Text style={[styles.tabText, isActive && styles.tabTextActive]}>
                {tab.label}
              </Text>
            </View>
          )
        })}
      </View>

      {/* Sharp previews (above lock) — always shown as peek content behind the centered card overlay */}
      <View style={styles.connectPreviewStack}>
        {sharpPreviews.map((p, i) => {
          const logo = getProviderLogo(p.provider)
          return (
            <View
              key={`sharp-${i}`}
              style={styles.connectPreviewCard}
              pointerEvents="none"
            >
              <View style={styles.connectPreviewUnreadCol}>
                {p.unread && <View style={styles.connectPreviewDot} />}
              </View>
              {logo ? (
                <Image source={logo} style={styles.connectPreviewLogo} resizeMode="contain" />
              ) : (
                <View style={styles.connectPreviewLogoFallback}>
                  <Text style={styles.connectPreviewLogoText}>{p.provider[0]}</Text>
                </View>
              )}
              <View style={{ flex: 1, minWidth: 0 }}>
                <View style={styles.connectPreviewTopRow}>
                  <Text
                    style={[styles.connectPreviewProvider, p.unread && { fontWeight: '700' }]}
                    numberOfLines={1}
                  >
                    {p.provider}
                  </Text>
                  <View style={[styles.connectPreviewTag, { backgroundColor: p.tagBg }]}>
                    <Text style={[styles.connectPreviewTagText, { color: p.tagColor }]}>{p.tag}</Text>
                  </View>
                  <View style={{ flex: 1 }} />
                  <Text style={styles.connectPreviewTime}>{p.time}</Text>
                </View>
                <Text style={styles.connectPreviewSubject} numberOfLines={1}>
                  {p.subject}
                </Text>
              </View>
            </View>
          )
        })}
      </View>

      {/* Locked card — ALWAYS an absolute overlay centered on the full viewport.
          Peek content (sharp + faded previews) is rendered behind it. */}
      <View
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, justifyContent: 'center', zIndex: 10 }}
        pointerEvents="box-none"
      >
      <View style={[
        styles.connectLockedWrap,
        // On mobile: edge-to-edge wrapper (no side padding) so card can stretch full width
        isMobile && { paddingHorizontal: 0 },
      ]} pointerEvents="box-none">
        <View style={[
          styles.connectLockedCard,
          // Mobile: full-width with thin side margin via wrapper padding, drop shadow to reduce noise
          isMobile && { maxWidth: 9999, marginHorizontal: spacing.sm, shadowOpacity: 0.04, elevation: 2 },
          // Desktop: cap at ~480px centered (focused card feel)
          isWideDesktop && { maxWidth: 480 },
        ]}>
          {/* Soft gradient backdrop inside card — adds polish without distraction */}
          <LinearGradient
            colors={[colors.background, '#f8fafc']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={StyleSheet.absoluteFillObject as any}
            pointerEvents="none"
          />
          <Animated.View
            style={[
              styles.connectLockedIcon,
              { width: lockIconSize, height: lockIconSize, borderRadius: lockIconSize / 2 },
              ctaAnimStyle,
            ]}
          >
            <Lock size={lockIconInnerSize} color={colors.primary} />
          </Animated.View>

          <Text style={[styles.connectLockedTitle, { fontSize: lockedTitleFontSize }]}>
            Forbind email
          </Text>
          <Text style={styles.connectLockedSubtitle}>
            for at se dine regninger
          </Text>

          <View style={styles.connectLockedBullets}>
            {benefits.map((label, i) => (
              <View key={i} style={styles.connectLockedBulletRow}>
                <View style={styles.connectLockedCheck}>
                  <Check size={12} color="#059669" strokeWidth={3} />
                </View>
                <Text style={styles.connectLockedBulletText}>{label}</Text>
              </View>
            ))}
          </View>

          {threadCount > 0 ? (
            <View style={styles.connectLockedUrgency}>
              <Bell size={13} color={colors.warningDark} />
              <Text style={styles.connectLockedUrgencyText}>
                {threadCount} {threadCount === 1 ? 'samtale venter allerede' : 'samtaler venter allerede'}
              </Text>
            </View>
          ) : hasActiveHunt ? (
            <View style={styles.connectLockedUrgency}>
              <Bell size={13} color={colors.warningDark} />
              <Text style={styles.connectLockedUrgencyText}>Jagten er i gang</Text>
            </View>
          ) : null}

          <Animated.View style={[styles.connectLockedCtaWrap, ctaAnimStyle]}>
            <PressableScale
              accessibilityRole="button"
              accessibilityLabel="Forbind email og find mine regninger"
              style={styles.connectLockedCta}
              onPress={onConnect}
              haptic="medium"
            >
              <Text style={styles.connectLockedCtaText}>Forbind email</Text>
              <ArrowRight size={16} color={colors.text.inverse} />
            </PressableScale>
          </Animated.View>

          <View style={styles.connectLockedTrust}>
            <Lock size={10} color={colors.text.muted} />
            <Text style={styles.connectLockedTrustText}>Vi læser kun — sender aldrig fra din email</Text>
          </View>
        </View>
      </View>
      </View>

      {/* Faded previews below — fill the page on desktop, hidden on mobile to let flex:1 wrapper center the card */}
      {fadedPreviews.length > 0 && (
      <View style={styles.connectPreviewStack}>
        {fadedPreviews.map((p, i) => {
          const logo = getProviderLogo(p.provider)
          // Progressive fade: 0.5 → 0.35 → 0.22 → 0.12
          const opacity = Math.max(0.12, 0.5 - i * 0.13)
          return (
            <View
              key={`faded-${i}`}
              style={[styles.connectPreviewCard, { opacity }]}
              pointerEvents="none"
            >
              <View style={styles.connectPreviewUnreadCol}>
                {p.unread && <View style={styles.connectPreviewDot} />}
              </View>
              {logo ? (
                <Image source={logo} style={styles.connectPreviewLogo} resizeMode="contain" />
              ) : (
                <View style={styles.connectPreviewLogoFallback}>
                  <Text style={styles.connectPreviewLogoText}>{p.provider[0]}</Text>
                </View>
              )}
              <View style={{ flex: 1, minWidth: 0 }}>
                <View style={styles.connectPreviewTopRow}>
                  <Text
                    style={[styles.connectPreviewProvider, p.unread && { fontWeight: '700' }]}
                    numberOfLines={1}
                  >
                    {p.provider}
                  </Text>
                  <View style={[styles.connectPreviewTag, { backgroundColor: p.tagBg }]}>
                    <Text style={[styles.connectPreviewTagText, { color: p.tagColor }]}>{p.tag}</Text>
                  </View>
                  <View style={{ flex: 1 }} />
                  <Text style={styles.connectPreviewTime}>{p.time}</Text>
                </View>
                <Text style={styles.connectPreviewSubject} numberOfLines={1}>
                  {p.subject}
                </Text>
              </View>
            </View>
          )
        })}
      </View>
      )}
    </View>
  )
}

/* ─── Thread Row ─── */

function ThreadRow({ thread, expanded, onPress }: { thread: InboxThread; expanded: boolean; onPress: () => void }) {
  const color = getProviderColor(thread.providerName)
  const logo = getProviderLogo(thread.providerName)
  const latest = thread.latestMessage
  const badges = getThreadBadges(thread)
  const hasAttachments = thread.messages.some(m => m.attachments && m.attachments.length > 0)

  return (
    <PressableScale
      accessibilityRole="button"
      accessibilityLabel={`${thread.providerName}${thread.hasUnread ? ', ulæst' : ''}: ${thread.subject}`}
      style={[styles.threadRow, thread.hasUnread && !expanded && styles.threadRowUnread, expanded && styles.threadRowExpanded]}
      onPress={onPress}
      haptic="light"
    >
      <View style={styles.unreadCol}>
        {thread.hasUnread && !expanded && <View style={styles.unreadDot} />}
      </View>

      {logo ? (
        <Image source={logo} style={styles.avatarLogo} />
      ) : (
        <View style={[styles.avatar, { backgroundColor: color.bg }]}>
          <Text style={[styles.avatarText, { color: color.text }]}>{thread.providerName.charAt(0)}</Text>
        </View>
      )}

      <View style={styles.threadContent}>
        <View style={styles.threadTopRow}>
          <Text style={[styles.threadName, thread.hasUnread && styles.threadNameBold]} numberOfLines={1}>
            {thread.providerName}
          </Text>
          <View style={styles.threadMeta}>
            {hasAttachments && <Paperclip size={12} color={colors.text.muted} />}
            {thread.messages.length > 1 && (
              <Text style={styles.msgCount}>{thread.messages.length}</Text>
            )}
            <Text style={styles.threadDate}>{formatShortDate(latest.received_at)}</Text>
          </View>
        </View>
        {badges.length > 0 && (
          <View style={styles.badgeRow}>
            {badges.map(b => (
              <View key={b.label} style={[styles.badge, { backgroundColor: b.bg }]}>
                <Text style={[styles.badgeText, { color: b.color }]}>{b.label}</Text>
              </View>
            ))}
          </View>
        )}
        {!expanded && (
          <>
            <Text style={[styles.threadSubject, thread.hasUnread && { fontWeight: '600', color: colors.text.primary }]} numberOfLines={1}>
              {thread.subject}
            </Text>
            <Text style={styles.threadPreview} numberOfLines={1}>
              {latest.direction === 'outbound' ? 'Du: ' : ''}{getMessagePreview(latest)}
            </Text>
          </>
        )}
      </View>

      <View style={styles.chevronCol}>
        <ChevronDown size={16} color={colors.text.muted} style={expanded ? undefined : { transform: [{ rotate: '-90deg' }] }} />
      </View>
    </PressableScale>
  )
}

/* ─── Thread Message (always shows content) ─── */

function ThreadMessage({
  msg, thread, onDownloadAttachment, onRespond,
}: {
  msg: Message; thread: InboxThread
  onDownloadAttachment: (msgId: string, att: any) => void; onRespond: () => void
}) {
  const isInbound = msg.direction === 'inbound'
  const bodyHtml = msg.body_html || ''
  const bodyText = (bodyHtml || msg.body || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
  const hasAttachments = msg.attachments && msg.attachments.length > 0
  const dateStr = new Date(msg.received_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })
  const timeStr = new Date(msg.received_at).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })

  return (
    <View style={styles.messageCard}>
      {/* Sender header */}
      <View style={styles.messageHeader}>
        <View style={[styles.messageDot, { backgroundColor: isInbound ? colors.primary : colors.text.muted }]} />
        <View style={styles.messageHeaderContent}>
          <Text style={styles.messageSender} numberOfLines={1}>
            {isInbound ? thread.providerName : 'Dig'}
          </Text>
          <Text style={styles.messageDate}>{dateStr} {timeStr}</Text>
        </View>
      </View>

      {/* Content */}
      <View style={styles.messageBody}>
        {msg.subject && (
          <Text style={styles.messageSubject}>{stripSubjectPrefix(msg.subject)}</Text>
        )}
        <EmailBody html={bodyHtml || bodyText} plainText={bodyText} maxPreview={100} />

        {hasAttachments && (
          <View style={styles.attachments}>
            {msg.attachments.map((att: any, ai: number) => (
              <Pressable
                key={ai}
                accessibilityRole="button"
                accessibilityLabel={`Download ${att.filename || att.name || `vedhæftning ${ai + 1}`}`}
                style={[styles.attachmentBadge, att.id && styles.attachmentBadgeActive]}
                onPress={() => onDownloadAttachment(msg.id, att)}
              >
                {att.id ? <Download size={12} color={colors.primary} /> : <Paperclip size={12} color={colors.text.muted} />}
                <Text style={[styles.attachmentName, att.id ? { color: colors.primaryDark } : { color: colors.text.muted, textDecorationLine: 'line-through' }]} numberOfLines={1}>
                  {att.id ? (att.filename || att.name || `Vedhæftning ${ai + 1}`) : 'Ikke tilgængelig'}
                </Text>
              </Pressable>
            ))}
          </View>
        )}

        {msg.parsed_data?.action_type === 'info_request' && (
          <ActionCard
            messageId={msg.id}
            infoType={msg.parsed_data.info_type || 'other'}
            question={msg.parsed_data.question || msg.body}
            responded={msg.parsed_data.responded}
            onRespond={onRespond}
          />
        )}
      </View>
    </View>
  )
}

/* ─── Styles ─── */

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.surface },
  header: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: spacing.md, backgroundColor: colors.background, borderBottomWidth: 1, borderBottomColor: colors.border,
  },
  title: { ...typography.h2, color: colors.text.primary },
  subtitle: { ...typography.small, color: colors.text.secondary, marginTop: 2 },
  refreshBtn: { width: 44, height: 44, alignItems: 'center', justifyContent: 'center' },
  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs, margin: spacing.md,
    padding: spacing.sm, backgroundColor: colors.warningLight, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.warningMuted,
  },
  errorText: { flex: 1, ...typography.small, color: '#92400e' },
  errorRetry: { ...typography.captionBold, color: '#92400e' },
  summaryBar: {
    flexDirection: 'row', gap: 6, paddingHorizontal: spacing.md, paddingTop: spacing.sm,
  },
  summaryCard: {
    flex: 1, backgroundColor: colors.background,
    borderRadius: borderRadius.md, paddingVertical: 10, paddingHorizontal: spacing.sm, alignItems: 'center',
    ...shadows.sm, borderWidth: 1, borderColor: colors.borderLight,
  },
  summaryValue: { ...typography.captionBold, fontSize: 15, color: colors.text.primary },
  summaryLabel: { ...typography.small, fontSize: 11, color: colors.text.secondary, marginTop: 1 },
  tabsWrap: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 6,
    paddingHorizontal: spacing.md, marginTop: spacing.sm, marginBottom: spacing.sm,
  },
  tab: {
    paddingHorizontal: 10, paddingVertical: 8, borderRadius: borderRadius.sm,
    backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border,
    minHeight: 44, justifyContent: 'center' as const,
  },
  tabActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  tabText: { ...typography.small, color: colors.text.secondary, fontWeight: '500' },
  tabTextActive: { color: colors.text.inverse },

  /* ── Thread row ── */
  threadRow: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 8,
    paddingRight: spacing.sm, paddingVertical: 14,
    backgroundColor: colors.background,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border,
  },
  threadRowUnread: { backgroundColor: colors.primarySubtle },
  threadRowExpanded: { backgroundColor: colors.surface, borderBottomWidth: 0 },
  unreadCol: { width: 18, paddingTop: 8, alignItems: 'center' },
  unreadDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: colors.primary },
  avatar: { width: 42, height: 42, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  avatarLogo: { width: 42, height: 42, borderRadius: 12 },
  avatarText: { fontWeight: '600', fontSize: 15 },
  threadContent: { flex: 1, minWidth: 0 },
  threadTopRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  threadName: { ...typography.bodyBold, color: colors.text.primary, flex: 1 },
  threadNameBold: { fontWeight: '700' },
  threadMeta: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  msgCount: { ...typography.small, color: colors.text.muted, backgroundColor: colors.surface, paddingHorizontal: 4, paddingVertical: 1, borderRadius: 4, overflow: 'hidden' as const },
  threadDate: { ...typography.caption, color: colors.text.muted },
  badgeRow: { flexDirection: 'row', gap: 4, marginTop: 3, flexWrap: 'wrap' },
  badge: { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 2, borderRadius: borderRadius.sm },
  badgeText: { fontSize: 11, fontWeight: '600' },
  threadSubject: { ...typography.captionBold, color: colors.text.secondary, marginTop: 4, fontWeight: '500' },
  threadPreview: { ...typography.small, color: colors.text.muted, marginTop: 2 },
  chevronCol: { paddingTop: 6, width: 20, alignItems: 'center' },

  /* ── Expanded thread container ── */
  expandedContainer: {
    backgroundColor: colors.surface,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
    paddingBottom: spacing.sm,
  },

  /* ── Message card (always visible) ── */
  messageCard: {
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.borderLight,
  },
  messageHeader: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    paddingHorizontal: spacing.md, paddingTop: 10, paddingBottom: 4,
  },
  messageDot: { width: 8, height: 8, borderRadius: 4 },
  messageHeaderContent: {
    flex: 1, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
  },
  messageSender: { ...typography.caption, color: colors.text.primary, fontWeight: '600' },
  messageDate: { ...typography.small, color: colors.text.muted },
  messageBody: {
    paddingHorizontal: spacing.md, paddingBottom: spacing.sm,
    borderLeftWidth: 2, borderLeftColor: colors.primaryLight, marginLeft: spacing.md + 4,
  },
  messageSubject: { ...typography.captionBold, color: colors.text.secondary, marginBottom: 2 },

  showOlderBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4,
    paddingVertical: spacing.xs, marginHorizontal: spacing.md,
    backgroundColor: colors.background, borderRadius: borderRadius.sm,
  },
  showOlderText: { ...typography.small, color: colors.primary, fontWeight: '500' },
  attachments: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.sm },
  attachmentBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 6,
    backgroundColor: colors.background, borderRadius: borderRadius.sm, borderWidth: 1, borderColor: colors.border,
    minHeight: 44,
  },
  attachmentBadgeActive: { backgroundColor: colors.primaryLight, borderColor: '#bfdbfe' },
  attachmentName: { ...typography.small, color: colors.text.secondary, maxWidth: 150 },
  replyButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs,
    marginHorizontal: spacing.md, marginTop: spacing.sm,
    paddingVertical: 12, backgroundColor: colors.primary, borderRadius: borderRadius.md,
  },
  replyButtonText: { ...typography.captionBold, color: colors.text.inverse },
  emptyTab: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl, gap: spacing.sm },
  emptyIcon: { width: 56, height: 56, borderRadius: 16, alignItems: 'center', justifyContent: 'center', marginBottom: spacing.sm },
  emptyTabTitle: { ...typography.bodyBold, color: colors.text.primary },
  emptyTabText: { ...typography.caption, color: colors.text.muted, textAlign: 'center' },
  // ═══ Connect-email empty state — locked peek layout ════════════════
  // Outer: flex 1, Preview stack (top) + LinearGradient overlay + floating locked card
  connectWrap: {
    flex: 1,
    backgroundColor: colors.surface,
    position: 'relative',
  },
  // Shared "muted" modifier — applies to mock summary bar + mock tabs
  // so they look like part of the locked state (not fully active UI).
  connectMockMuted: {
    opacity: 0.85,
  },
  // Preview stack — realistic "peek" behind the lock
  connectPreviewStack: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    gap: 8,
  },
  // Preview row — matches real ThreadRow: logo + provider + tag + time + subject
  connectPreviewCard: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 8,
    backgroundColor: colors.background, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.borderLight,
    paddingVertical: 10, paddingRight: spacing.sm, paddingLeft: 4,
  },
  connectPreviewFaded: { opacity: 0.5 },
  connectPreviewUnreadCol: { width: 14, paddingTop: 6, alignItems: 'center' },
  connectPreviewDot: {
    width: 8, height: 8, borderRadius: 4, backgroundColor: colors.primary,
  },
  connectPreviewLogo: { width: 36, height: 36, borderRadius: 10 },
  connectPreviewLogoFallback: {
    width: 36, height: 36, borderRadius: 10,
    backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border,
    alignItems: 'center', justifyContent: 'center',
  },
  connectPreviewLogoText: { ...typography.captionBold, color: colors.text.secondary, fontSize: 14 },
  connectPreviewTopRow: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
  },
  connectPreviewProvider: { ...typography.captionBold, color: colors.text.primary, flexShrink: 0 },
  connectPreviewTag: {
    alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 2,
    borderRadius: borderRadius.sm,
  },
  connectPreviewTagText: { fontSize: 11, fontWeight: '600' },
  connectPreviewTime: { ...typography.small, color: colors.text.muted, fontSize: 11 },
  connectPreviewSubject: {
    ...typography.small, color: colors.text.secondary, marginTop: 3, fontWeight: '500',
  },
  // Locked card wrapper — INLINE now (not absolute), sits between sharp and faded previews
  // The shadows.lg on the card itself gives visual separation from the faded content around it.
  connectLockedWrap: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    alignItems: 'center',
    zIndex: 2, // above faded previews if they overlap
  },
  // Locked card — the unlock moment
  connectLockedCard: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: colors.background,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    alignItems: 'center',
    ...shadows.lg,
    gap: spacing.xs,
    overflow: 'hidden', // clip LinearGradient backdrop to rounded corners
  },
  connectLockedIcon: {
    backgroundColor: colors.primaryLight,
    alignItems: 'center', justifyContent: 'center',
    marginBottom: spacing.xs,
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 4 },
  },
  connectLockedTitle: {
    fontWeight: '700', color: colors.text.primary, letterSpacing: -0.4, textAlign: 'center',
  },
  connectLockedSubtitle: {
    ...typography.caption, color: colors.text.secondary, textAlign: 'center',
    marginBottom: spacing.sm,
  },
  connectLockedBullets: {
    alignSelf: 'stretch',
    gap: 8,
    marginBottom: spacing.sm,
  },
  connectLockedBulletRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
  },
  connectLockedCheck: {
    width: 20, height: 20, borderRadius: 10,
    backgroundColor: '#d1fae5',
    alignItems: 'center', justifyContent: 'center',
  },
  connectLockedBulletText: {
    ...typography.caption, color: colors.text.primary, fontWeight: '500', flex: 1,
  },
  connectLockedUrgency: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: spacing.sm, paddingVertical: 6,
    backgroundColor: '#fef3c7', borderRadius: borderRadius.sm,
    marginBottom: spacing.xs,
    alignSelf: 'stretch',
    justifyContent: 'center',
  },
  connectLockedUrgencyText: {
    ...typography.small, color: colors.warningDark, fontWeight: '600', fontSize: 12,
  },
  connectLockedCtaWrap: {
    alignSelf: 'stretch',
  },
  connectLockedCta: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
    backgroundColor: colors.primary, paddingVertical: 14, borderRadius: borderRadius.md,
    ...shadows.colored(colors.primary),
  },
  connectLockedCtaText: { ...typography.captionBold, color: colors.text.inverse, fontSize: 16 },
  connectLockedTrust: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    marginTop: spacing.xs,
  },
  connectLockedTrustText: {
    ...typography.small, color: colors.text.muted, fontSize: 11,
  },

  emptyInbox: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl, gap: spacing.sm },
  emptyInboxIcon: {
    width: 56, height: 56, borderRadius: 16, backgroundColor: colors.borderLight,
    alignItems: 'center', justifyContent: 'center', marginBottom: spacing.sm,
  },
  emptyInboxTitle: { ...typography.bodyBold, color: colors.text.primary },
  emptyInboxText: { ...typography.caption, color: colors.text.muted, textAlign: 'center', maxWidth: 280 },
  emptyInboxCta: {
    marginTop: spacing.sm, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm,
    backgroundColor: colors.primary, borderRadius: borderRadius.md,
  },
  emptyInboxCtaText: { ...typography.captionBold, color: colors.text.inverse },
})
